import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  adminGetTutorSorting,
  adminSyncTutorSorting,
  adminUpdateTutorSorting,
} from './adminApi';

export interface ITutorSortingTutor {
  _id: string;
  firstname: string;
  lastname: string;
  country?: string;
}

export interface IManualSorting {
  tutor: ITutorSortingTutor;
  sortingKey?: number;
  country?: string;
}

export interface ITutorSortingBadge {
  tutor: ITutorSortingTutor;
  badge: string;
  color: string;
  country?: string;
}

export interface ITutorSorting {
  _id: string;
  tenant: string;
  tutors: Array<{
    tutor: ITutorSortingTutor;
    amount: number;
    currency: string;
  }>;
  filters?: { curriculum?: string; subject?: string; grade?: string };
  manualSorting?: Array<IManualSorting>;
  badges?: Array<ITutorSortingBadge>;
  updatedAt?: string;
}

export interface IInitialTutorSortingState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminTutorSortingListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITutorSorting>;
  };
}

const initialState: IInitialTutorSortingState = {
  status: 'idle',
  adminTutorSortingListData: undefined,
};

export const TutorSortingSlice = createSlice({
  initialState,
  name: 'tutorSorting',
  reducers: {
    resetAdminTutorSortingListData: (state) => {
      state.adminTutorSortingListData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetTutorSorting.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminTutorSortingListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminTutorSortingListData = newAdminTutorSortingListData;
      })
      .addCase(adminUpdateTutorSorting.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminTutorSortingListData &&
          state.adminTutorSortingListData.list &&
          state.adminTutorSortingListData.list.length
        ) {
          const newAdminTutorSortingListData = {
            ...state.adminTutorSortingListData,
          };

          const index = newAdminTutorSortingListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminTutorSortingListData.list[index] = result;
          }

          state.adminTutorSortingListData = newAdminTutorSortingListData;
        }
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminGetTutorSorting,
        adminUpdateTutorSorting,
        adminSyncTutorSorting
      ),
      (state) => {
        state.status = 'loading';
      }
    );
    builder.addMatcher(
      isFulfilled(
        adminGetTutorSorting,
        adminUpdateTutorSorting,
        adminSyncTutorSorting
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminGetTutorSorting,
        adminUpdateTutorSorting,
        adminSyncTutorSorting
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getTutorSortingState = (
  state: RootState
): RootState['tutorSorting'] => state.tutorSorting;

export const { resetAdminTutorSortingListData } = TutorSortingSlice.actions;

export * from './adminApi';
export default TutorSortingSlice.reducer;
